import React, { createContext, useCallback, useEffect, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import type { User } from '../types/auth';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  completeLogin: (user: User) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    setIsLoading(true);
    try { setUser(await authService.getCurrentUser()); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { void refreshUser(); }, [refreshUser]);

  const logout = useCallback(async () => {
    try { await authService.logout(); }
    finally { setUser(null); navigate('/login'); }
  }, [navigate]);

  return <AuthContext.Provider value={{
    user, isAuthenticated: !!user, isLoading, completeLogin: setUser, logout, refreshUser,
  }}>{children}</AuthContext.Provider>;
};

export default AuthContext;
